import {
  type EstimateLineItem,
  type NodeItem,
  selectNodeChildren,
  selectNodeDescentChildren,
  selectNodeParent,
} from '@octopus-do/octopus-project-core'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import type { AppState } from '@store/types'

import type { EstimateTreeNode } from '../types'

export const getUniqueNodeIdsWithDescendants = (
  ids: string[],
  state: AppState
): string[] => {
  const acc = new Set<string>()

  for (const id of ids) {
    const descends = selectNodeDescentChildren(id)(state)

    for (const descendId of descends) {
      acc.add(descendId)
    }
  }

  return Array.from(acc)
}

export const buildEstimateTreeNode = (
  id: string,
  state: AppState,
  nodeById: Map<string, NodeItem>,
  lineItems: EstimateLineItem[]
): EstimateTreeNode => {
  const childIds = selectNodeChildren(id)(state)
  const parentId = selectNodeParent(id)(state)[0] || null
  const node = nodeById.get(id)

  if (!node) {
    throw new Error(`Node not found: ${id}`)
  }

  const nodeEstimates =
    migrationProjectSelector.getProjectNodeEstimates(id)(state)

  const estimates = lineItems.map((item) => {
    const estimate = nodeEstimates.find((e) => e.lineItemId === item.id)
    const value = estimate ? estimate.value : 0
    return { ...item, value }
  })

  return {
    id: node?.id,
    title: node?.title || 'Page',
    parentId,
    estimates,
    children: childIds.map((childId) =>
      buildEstimateTreeNode(childId, state, nodeById, lineItems)
    ),
  }
}

export const getRootNodeIds = (ids: string[], state: AppState): string[] => {
  const childSet = new Set<string>()

  for (const id of ids) {
    const children = selectNodeChildren(id)(state)
    for (const childId of children) {
      childSet.add(childId)
    }
  }

  return ids.filter((id) => !childSet.has(id))
}

export const createNodeByIdMap = (nodes: NodeItem[]): Map<string, NodeItem> => {
  return new Map(nodes.map((n) => [n.id, n] as const))
}
